<script setup lang="ts">
import { computed, ref, onMounted, nextTick } from 'vue';
import Button from 'primevue/button';
import ToggleSwitch from 'primevue/toggleswitch';
import Menu from 'primevue/menu';
import MultiSelect from '@apparatix/library/MultiSelect.vue';
import Chart from 'primevue/chart';
import Dialog from 'primevue/dialog';
import Select from 'primevue/select';
import type { ChartOptions } from 'chart.js';
import type { MenuItem } from 'primevue/menuitem';
import { useStringFunctions, useDateFns, useEndpoint, useToastNotifications } from '@apparatix/composables';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import FormSkeleton from '@apparatix/Components/FormSkeleton.vue';
import type {
  ProjectionsPayload,
  Office,
  OpportunityType,
  OptionsUser,
  RevenueProjection,
  RevenueProjectionResponse,
  RevenueProjectionUserResponse,
  ByCampaignByMonthResponse,
  ProjectedDataItem,
  ProjectionsDataset,
} from '@apparatix/types/Leads';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import DownloadIcon from '@primeicons/vue/download';
import WindowMaximizeIcon from '@primeicons/vue/window-maximize';
import EllipsisHIcon from '@primeicons/vue/ellipsis-h';
import FileExportIcon from '@primeicons/vue/file-export';

const props = defineProps<{ type: OpportunityType }>();
const store = useOpportunityStore();
const projections = ref<RevenueProjection[]>([]);
const byCampaignProjections = ref<ProjectedDataItem[]>([]);
const dateRange = ref<string[]>([]);
const dt = ref();
const currentUser = ref<OptionsUser>(store.currentUser);
const style = ref<CSSStyleDeclaration>();
const includeSuspended = ref(false);
const showChartModal = ref(false);

// @TODO: We rely on the store for offices and re-init the user each time it opens. Nothing wrong, but it's inconsistent.
// @TODO: Might be good to confirm desired interaction for user.
const selectedUser = ref<OptionsUser>();
const currentlySelectedOfficesForProjections = ref<Office[]>(store.currentlySelectedOfficesForProjections);
const allUsers = ref<OptionsUser[]>([]);
const allOffices = ref(store.options[props.type].offices);

const moreMenuOptions = ref<MenuItem[]>([
  {
    label: 'Export',
    items: [
      {
        label: 'Image (.png)',
        icon: DownloadIcon,
        command: () => exportGraph(),
      },
      {
        label: 'Spreadsheet (.csv)',
        icon: DownloadIcon,
        command: () => exportToCSV(),
      },
    ],
  },
]);

const onDialogShow = () => {
  nextTick(() => {
    if (myChart.value && myChart.value.chart) {
      myChart.value.chart.resize();
      myChart.value.chart.update();
    }
  });
};

const handleUpdate = () => {
  updateStore();
  fetchProjections();
};

const updateStore = () => {
  store.currentlySelectedOfficesForProjections = currentlySelectedOfficesForProjections.value;
};

const {
  makeRequest: makeUserRequest,
} = useEndpoint<RevenueProjectionUserResponse>({
  url: '/crm/opportunities/projections-users',
  type: 'GET',
  successField: 'data',
  startInLoadingState: true,
  toast: { showOnSuccess: false },
  onSuccess: response => {
    allUsers.value = response.data.users;
  },
});

const {
  isLoading, makeRequest, didError,
} = useEndpoint<RevenueProjectionResponse>({
  url: '/crm/opportunities/projections',
  type: 'POST',
  successField: 'data',
  startInLoadingState: true,
  toast: { showOnSuccess: false },
  onSuccess: response => {
    projections.value = response.data.projectedData;
  },
});

const {
  isLoading: isByCampaignLoading, makeRequest: makeByCampaignRequest,
} = useEndpoint<ByCampaignByMonthResponse>({
  url: '/crm/opportunities/projections-by-campaign',
  type: 'POST',
  successField: 'data',
  startInLoadingState: true,
  toast: { showOnSuccess: false },
  onSuccess: response => {
    byCampaignProjections.value = response.data.projectedData;
    dateRange.value = response.data.dateRange;
  },
});

const fetchProjections = () => {
  const payload: ProjectionsPayload = {
    offices: officeIds.value, userId: selectedUserId.value, allUsers: allUsers.value, isManagement: store.hasProjectionsManagerView, currentUser: currentUser.value,
  };
  makeRequest(payload);
  makeByCampaignRequest(payload);
};
const myChart = ref();
const moreMenuRef = ref();

const showExportButton = computed(() => {
  const el = document.createElement('a');
  return el.download !== undefined;
});

const exportGraph = () => {
  const chartInstance = myChart.value.chart;

  const canvas = chartInstance.canvas;

  const imageData = canvas.toDataURL('image/png');

  const link = document.createElement('a');
  link.href = imageData;

  const timestampString = formatDate(new Date(), getTimestampFormat());
  const filename = `opportunity_projections_graph_${timestampString}`;
  link.download = `${filename}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const { showError } = useToastNotifications();

const exportToCSV = () => {
  // **Prepare CSV Headers**
  const headers = ['Month'];
  const datasetLabels = dataset.value.datasets.map(
    (dataset) => dataset.label,
  );
  headers.push(...datasetLabels);

  // **Prepare CSV Rows**
  const rows = dataset.value.labels.map((label, index) => {
    const row: (string | number)[] = [label];
    dataset.value.datasets.forEach((dataset) => {
      row.push(dataset.data[index]);
    });
    return row;
  });

  // **Combine Headers and Rows**
  const csvContent =
        [headers, ...rows].map((e) => e.join(',')).join('\n');

  // **Create a Blob and Trigger Download**
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    // Feature detection for download attribute
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);

    const timestampString = formatDate(new Date(), getTimestampFormat());
    const filename = `opportunity_projections_chart_${timestampString}.csv`;

    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    // Fallback for browsers that do not support the download attribute
    showError('Your browser does not support downloading files via this method.');
  }
};

const { formatDate, getTimestampFormat } = useDateFns();

const { formatAsCurrency } = useStringFunctions();
const areAllValuesZero = (values: number[]) => values.every(value => value === 0);

const options: ChartOptions<'bar'> = {
  scales: {
    x: { stacked: true },
    y: {
      stacked: true,
      ticks: { callback: (value: number) => formatAsCurrency(value, false) },
    },
  },
  interaction: {
    mode: 'index',
    intersect: true,
  },
  plugins: {
    tooltip: {
      mode: 'index',
      intersect: true,
      usePointStyle: true,
      boxWidth: 16,
      boxHeight: 16,
      callbacks: {
        labelPointStyle: ctx => ({
          pointStyle: (ctx.dataset as any).pointStyle,
          rotation: 0,
        }),
      },
    },
    legend: {
      labels: {
        usePointStyle: true,
        pointStyleWidth: 40,
      },
    },
  },
};

const expandedOptions: ChartOptions<'bar'> = {
  scales: {
    x: { stacked: true },
    y: {
      stacked: true,
      ticks: { callback: (value: number) => formatAsCurrency(value, false) },
    },
  },
  plugins: {
    tooltip: {
      mode: 'index',
      intersect: true,
      usePointStyle: true,
      boxWidth: 16,
      boxHeight: 16,
      callbacks: {
        labelPointStyle: ctx => ({
          pointStyle: (ctx.dataset as any).pointStyle,
          rotation: 0,
        }),
      },
    },
    legend: {
      labels: {
        usePointStyle: true,
        pointStyleWidth: 40,
      },
    },
  },
  maintainAspectRatio: false,
  responsive: true,
  layout: { padding: 0 },
};

const dataset = computed(() => {
  if (!projections.value || !style.value) return null;

  const sold = projections.value.map(projection => projection.sold);
  const projected = projections.value.map(projection => projection.projected);
  // const overage = projections.value.map(projection => projection.overage);
  const deficit = projections.value.map(projection => projection.deficit);
  const budget = projections.value.map(projection => projection.budget);

  const data: { labels: string[], datasets: ProjectionsDataset[] } = {
    labels: projections.value.map(projection => projection.yearMonth),
    datasets: [
      {
        type: 'bar',
        label: 'Sold',
        backgroundColor: style.value.getPropertyValue('--primary-green'),
        data: sold,
        pointStyle: 'rect',
      },
      {
        type: 'bar',
        label: 'Projected',
        backgroundColor: style.value.getPropertyValue('--primary-blue-300'),
        data: projected,
        pointStyle: 'rect',
      },
      // {
      //   type: 'bar',
      //   label: 'Overage',
      //   backgroundColor: style.value.getPropertyValue('--info-background'),
      //   data: overage,
      // },
      {
        type: 'bar',
        label: 'Deficit',
        backgroundColor: style.value.getPropertyValue('--danger-background'),
        data: deficit,
        pointStyle: 'rect',
      },
    ],
  };
  if (!areAllValuesZero(budget)) {
    data.datasets.unshift({
      type: 'line',
      label: getBudgetLabel(),
      borderColor: style.value.getPropertyValue('--primary-text-blue'),
      borderWidth: 2,
      fill: false,
      backgroundColor: 'transparent',
      tension: 0.5,
      data: budget,
      pointStyle: 'line',
      pointRadius: 0,
      pointHoverRadius: 5,
    });
  }

  return data;
});

const getBudgetLabel = () => !selectedUserId.value && store.hasProjectionsManagerView ? 'Venue Budget(s)' : 'AE Budget(s)';

const shouldShowGraph = computed(() => (! didError.value && ! isLoading.value));

const opportunityTotals = computed(() => projections.value.reduce((total, item) => total + item.opportunityTotals, 0));
const opportunityTotalsAdjusted = computed(() => projections.value.reduce((total, item) => total + item.projected, 0));

const officeLength = computed (() => currentlySelectedOfficesForProjections.value.length);
const officeLabel = computed(() => {
  if (officeLength.value === 0) {
    return 'No Offices Selected';
  }

  if (officeLength.value === 1) {
    return `${officeLength.value} Office Selected`;
  }

  return `${officeLength.value} Offices Selected`;
});

const selectedUserId = computed(() => !selectedUser.value?.id ? 0 : selectedUser.value.id);
const officeIds = computed(() => currentlySelectedOfficesForProjections.value.map(office => office.id));

// Check available in list and hide dropdown if only single office in settings
const officeOptionsLength = computed(() => store.options[props.type].offices.length);
const showOfficeDropdown = computed(() => officeOptionsLength.value > 1);

const onMoreButtonClick = (e: MouseEvent) => moreMenuRef.value.toggle(e);
onMounted(async () => {
  await makeUserRequest();
  fetchProjections();
  style.value = getComputedStyle(document.documentElement);
});

const filteredUsers = computed(() => includeSuspended.value ? allUsers.value : allUsers.value.filter(user => user.isActive));

const totalByColumn = computed(() => {
  const totals = {};
  dateRange.value.forEach(date => {
    totals[date] = 0;
  });
  byCampaignProjections.value.forEach(row => {
    dateRange.value.forEach(date => {
      totals[date] += Number(row[date]) || 0;
    });
  });
  return totals;
});

const formatFooter = (value) => formatAsCurrency(value.toFixed(2));

const exportCSV = () => {
  dt.value.exportCSV();
};

</script>

<template>
  <div class="flex flex-wrap w-full p-6 gap-6">
    <!-- Dropdowns Group -->
    <div class="flex flex-wrap w-full md:flex-nowrap md:w-auto gap-6">
      <div v-if="showOfficeDropdown" class="w-full md:w-auto">
        <MultiSelect
          v-model="currentlySelectedOfficesForProjections"
          filter
          :disabled="isLoading"
          :options="allOffices"
          :placeholder="officeLabel"
          :selected-label="officeLabel"
          option-label="office"
          @update:model-value="handleUpdate"
        />
      </div>
      <div v-if="store.hasProjectionsManagerView" class="w-full md:w-auto">
        <Select
          v-model="selectedUser"
          filter
          show-clear
          :disabled="isLoading"
          :options="filteredUsers"
          placeholder="Select User"
          option-label="fullName"
          @update:model-value="handleUpdate"
        >
          <template #option="slotProps">
            <div :class="{ 'disabled-text': !slotProps.option.isActive }">
              {{ slotProps.option.fullName }}
            </div>
          </template>
        </Select>
      </div>
      <div v-if="store.hasProjectionsManagerView" class="flex w-full items-center md:w-auto">
        <ToggleSwitch v-model="includeSuspended" />
        <span class="pl-2">
          Include Suspended
        </span>
      </div>
    </div>

    <!-- Spacer -->
    <div class="flex-grow" />

    <!-- Text Outputs and Buttons Group -->
    <div class="flex items-center w-full md:w-auto">
      <span>Total:</span>
      <div class="px-0.5" />
      <span class="text-[var(--swimlane-secondary-text)]">
        {{ formatAsCurrency(opportunityTotals) }}
      </span>
    </div>
    <div class="flex items-center w-full md:w-auto">
      <span>CI Adjusted:</span>
      <div class="px-0.5" />
      <span class="text-[var(--swimlane-secondary-text)]">
        {{ formatAsCurrency(opportunityTotalsAdjusted) }}
      </span>
    </div>
    <div class="w-full md:w-auto">
      <Button
        icon-only
        aria-label="Expand"
        size="small"
        severity="secondary"
        outlined
        :disabled="isLoading"
        @click="showChartModal = true"
      >
        <WindowMaximizeIcon />
      </Button>
      <Dialog
        v-model:visible="showChartModal"
        :modal="true"
        class="w-[90vw] max-w-[90wv] m-0 p-0 h-full"
        content-class="p-0 overflow-hidden flex flex-col h-full"
        header=" "
        :closable="true"
        @show="onDialogShow"
      >
        <div class="flex-auto h-full m-0 p-0">
          <Chart
            ref="expandedChart"
            type="bar"
            :data="dataset"
            :options="expandedOptions"
          />
        </div>
      </Dialog>
    </div>
    <div v-if="showExportButton" class="w-full md:w-auto">
      <Button
        icon-only
        aria-label="More options"
        size="small"
        severity="secondary"
        outlined
        :disabled="isLoading"
        @click="onMoreButtonClick"
      >
        <EllipsisHIcon />
      </Button>
    </div>
  </div>
  <div class="w-full">
    <FormSkeleton v-if="isLoading" />
    <div
      v-if="shouldShowGraph"
      class="w-full h-auto pt-8 px-2"
      style="position: relative;"
    >
      <Chart
        ref="myChart"
        type="bar"
        :data="dataset"
        :options="options"
      />
    </div>
    <FormSkeleton v-if="isByCampaignLoading" />
    <div v-if="!isByCampaignLoading" class="flex flex-col mt-3">
      <div class="flex justify-end mb-4">
        <Button
          v-tooltip.left="'Export'"
          outlined
          severity="secondary"
          class="flex items-center"
          @click="exportCSV()"
        >
          <FileExportIcon />
          <span class="font-bold">Export</span>
        </Button>
      </div>
      <DataTable ref="dt" :value="byCampaignProjections">
        <Column field="id" header="ID" hidden />
        <Column field="name" header="Name" sortable />
        <Column field="account" header="Account" sortable />
        <Column field="owner" header="Owner" sortable />
        <Column field="office" header="Office" sortable />
        <Column field="interval" header="Interval" sortable />
        <Column field="rate" header="Interval Rate" sortable>
          <template #body="{ data }">
            {{ formatAsCurrency(data['rate']) }}
          </template>
        </Column>
        <Column field="periods" header="Periods" sortable />
        <Column field="start" header="Anticipated Start" sortable />
        <Column field="confidence" header="CI Pct" sortable />
        <Column
          v-for="date in dateRange"
          :key="date"
          :field="date"
          :header="date"
          :footer="formatFooter(totalByColumn[date])"
          sortable
        >
          <template #body="{ data }">
            {{ formatAsCurrency(data[date]) }}
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
  <Menu ref="moreMenuRef" :model="moreMenuOptions" :popup="true" />
</template>

<style scoped>
.p-dialog .p-chart {
  width: 100%;
  height: 100%;
}

.p-button-secondary {
  background-color: lightgray;
  background: var(--white);
  border: 1px solid var(--black-16);
  color: var(--black-80);
}

/*
 * #a1a1a1 was suggested as the PrimeVue default disabled color
 */
.disabled-text {
  color: #a1a1a1;
}
</style>
